"use client";

import Image from "next/image";
import { Icon } from "@repo/ui/icon";
import { Link } from "@/i18n/navigation";
import { AudienceTabs } from "@/components/navigation/audience-tabs.component";
import { useNavigationState } from "@/components/navigation/navigation-provider";
import { useHomeHref } from "@/components/navigation/use-home-href";
import { useAudience } from "@/components/common/audience";

interface MobileHeaderViewProps {
  logoSrc: string;
  logoAlt: string;
  homeHref: string;
  isOpen: boolean;
  onToggle: () => void;
  onSearch: () => void;
}

export function MobileHeaderView({
  logoSrc,
  logoAlt,
  homeHref,
  isOpen,
  onToggle,
  onSearch,
}: MobileHeaderViewProps) {
  return (
    <div className="desktop-lg:hidden">
      <div className="border-b border-border-subtle">
        <AudienceTabs variant="mobile" />
      </div>
      <div className="flex h-16 items-center justify-between px-4">
        {/* Logo */}
        <Link href={homeHref} className="flex-shrink-0">
          <Image
            src={logoSrc}
            alt={logoAlt}
            width={120}
            height={32}
            className="h-8 w-[120px]"
          />
        </Link>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Search button */}
          <button
            type="button"
            onClick={onSearch}
            className="flex h-10 w-10 items-center justify-center rounded-md text-text-primary transition-colors hover:bg-surface-base-secondary"
            aria-label="Open search"
          >
            <Icon name="search" size="lg" />
          </button>

          {/* Hamburger / Close button */}
          <button
            type="button"
            onClick={onToggle}
            className="flex h-10 w-10 items-center justify-center rounded-md text-text-primary transition-colors hover:bg-surface-base-secondary"
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
          >
            {isOpen ? (
              <Icon name="close" size="lg" />
            ) : (
              <Icon name="menu" size="lg" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MobileHeader() {
  const {
    navigationData,
    isMobileMenuOpen,
    isSearchOpen,
    openMobileMenu,
    closeMobileMenu,
    openSearch,
  } = useNavigationState();

  const { activeTab } = useAudience();
  const homeHref = useHomeHref();

  if (isSearchOpen) return null;

  const isBusinesses = activeTab === "businesses";
  const logo = navigationData.logo;
  const logoSrc = isBusinesses
    ? (logo?.dark ?? logo?.light ?? "/images/va-waterfront-logo-light.svg")
    : (logo?.light ?? "/images/va-waterfront-logo.svg");

  return (
    <MobileHeaderView
      logoSrc={logoSrc}
      logoAlt={logo?.alt ?? "V&A Waterfront Cape Town"}
      homeHref={homeHref}
      isOpen={isMobileMenuOpen}
      onToggle={isMobileMenuOpen ? closeMobileMenu : openMobileMenu}
      onSearch={openSearch}
    />
  );
}
